// Combination Matrix — feature x domain co-retrieval grid + cell detail panel,
// per mockup screen2-matrix.png. Story #4937 Stream 2c; Story #5305 Task
// #5324 (backend) / Task #5326 Stream 11b (this file) wires the REAL v2
// contract in (design §5.3 "The Combination Matrix becomes honest").
//
// Four honest states, all real (never a fabricated grid):
//   'complete'            — cells + a legend RE-DERIVED from this matrix's
//                            own real share distribution (whitespace/
//                            matrix.py::_derive_legend) — never the old
//                            fixed, corpus-volume-sized count bands.
//   'no_components'       — the project has no extracted idea-components yet.
//                            Shows a consent-gated "Extract features first ·
//                            uses AI credits" CTA, hidden below edit
//                            permission (decompose_action.edit_allowed is a
//                            REAL server-side check_permission() result).
//   'no_scan_yet'         — no completed /scan cache to build domains from.
//   'no_domain_metadata'  — cached records had no resolvable CPC/topic
//                            metadata to place on the domain axis.
//
// Grid + legend are hand-rolled CSS grid (amendment G: no charting library
// in package.json — only cytoscape, and this view doesn't need it).
import { useEffect, useRef, useState } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import { buildMatrixGrid, densityBandColor } from '../../lib/whitespace'
import type { WhitespaceMatrix, WhitespaceMatrixCell } from '../../lib/whitespace'
import { requestWhitespaceDecompose } from '../../services/api'
import EmptyState from '../shared/EmptyState'
import HelpPopover from '../help/HelpPopover'
import HowToReadThisCard from '../help/HowToReadThisCard'
import {
  DECOMPOSE_GAVE_UP_MESSAGE,
  DECOMPOSE_POLL_MAX_MS,
  DECOMPOSE_POLL_MS,
  DECOMPOSE_STILL_RUNNING_MESSAGE,
  MATRIX_LABEL_COLUMN_PX,
  MATRIX_SCROLL_HINT_MIN_DOMAINS,
  decomposeErrorMessage,
  isDecomposeRunningResponse,
  isDecomposeStillRunning,
} from './decomposeStatus'


function cellKey(c: Pick<WhitespaceMatrixCell, 'feature_id' | 'domain_id'>): string {
  return `${c.feature_id}::${c.domain_id}`
}

/** Honest, data-derived narrative for the selected cell — no field invented
 * beyond what the matrix contract carries (share, count, band, labels). */
function cellNarrative(cell: WhitespaceMatrixCell): string {
  const pct = cell.share != null ? Math.round(cell.share * 100) : null
  if (cell.is_white_space) {
    return `No meaningful overlap in the scanned neighbourhood between ${cell.feature_label} and ${cell.domain_label} — both halves exist separately, the pairing doesn't (this feature's matches concentrate elsewhere).`
  }
  const shareClause = pct != null ? `${pct}% of ${cell.feature_label}'s matched neighbourhood` : `${cell.count} record${cell.count === 1 ? '' : 's'}`
  return `${shareClause} falls in ${cell.domain_label} (${cell.count} record${cell.count === 1 ? '' : 's'} above the similarity bar, ${cell.density_band} density).`
}

interface CombinationMatrixProps {
  matrix: WhitespaceMatrix | null
  projectId: string
  onExploreAsOpportunity?: (cell: WhitespaceMatrixCell) => void
  onRunNoveltyProbe?: (cell: WhitespaceMatrixCell) => void
  onDecomposed?: () => void
  /** The decompose request. Defaults to the real API call; tests inject a
   * real function that fails the way the gateway does (no module mocks —
   * documentation/LIVE-TESTING-MANDATE.md). */
  requestDecompose?: (route: string) => Promise<unknown>
}

export default function CombinationMatrix({ matrix, projectId, onExploreAsOpportunity, onRunNoveltyProbe, onDecomposed, requestDecompose }: CombinationMatrixProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [isDecomposing, setIsDecomposing] = useState(false)
  const [decomposeError, setDecomposeError] = useState<string | null>(null)
  const [decomposeNotice, setDecomposeNotice] = useState<string | null>(null)
  const [awaitingDecompose, setAwaitingDecompose] = useState(false)
  // Latest onDecomposed without re-arming the poll on every parent render.
  const onDecomposedRef = useRef(onDecomposed)
  onDecomposedRef.current = onDecomposed
  const matrixStatus = matrix?.status

  // While the server is still extracting (gateway timeout above), re-read the
  // matrix every DECOMPOSE_POLL_MS; stop the moment it leaves no_components,
  // or give up honestly after DECOMPOSE_POLL_MAX_MS.
  useEffect(() => {
    if (!awaitingDecompose) return
    if (matrixStatus && matrixStatus !== 'no_components') {
      setAwaitingDecompose(false)
      setIsDecomposing(false)
      setDecomposeNotice(null)
      return
    }
    const poll = window.setInterval(() => onDecomposedRef.current?.(), DECOMPOSE_POLL_MS)
    const giveUp = window.setTimeout(() => {
      window.clearInterval(poll)
      setAwaitingDecompose(false)
      setIsDecomposing(false)
      setDecomposeNotice(null)
      setDecomposeError(DECOMPOSE_GAVE_UP_MESSAGE)
    }, DECOMPOSE_POLL_MAX_MS)
    return () => {
      window.clearInterval(poll)
      window.clearTimeout(giveUp)
    }
  }, [awaitingDecompose, matrixStatus])

  if (!matrix || matrix.status === 'no_scan_yet') {
    return (
      <div data-tour="combination-matrix" data-testid="combination-matrix-empty">
        <EmptyState
          icon={<Sparkles className="w-7 h-7" />}
          title="No combination data yet"
          description="Run a scan to populate the matrix."
        />
      </div>
    )
  }

  if (matrix.status === 'no_domain_metadata') {
    return (
      <div data-tour="combination-matrix">
        <EmptyState
          icon={<Sparkles className="w-7 h-7" />}
          title="No placeable domains found"
          description="Your scan's neighbourhood didn't carry any resolvable CPC codes or subject topics to build the domain axis from — this doesn't mean the corpus is empty, just that this scan's cached records aren't taggable."
        />
      </div>
    )
  }

  if (matrix.status === 'no_components') {
    const action = matrix.decompose_action
    const handleDecompose = async () => {
      setDecomposeError(null)
      setDecomposeNotice(null)
      setIsDecomposing(true)
      try {
        const res = await (requestDecompose ?? requestWhitespaceDecompose)(action?.route || `/crosspollination/decompose/${projectId}`)
        if (isDecomposeRunningResponse(res)) {
          // Bug #5861: queued on the server — keep the spinner, say so, poll.
          setDecomposeNotice(DECOMPOSE_STILL_RUNNING_MESSAGE)
          setAwaitingDecompose(true)
        } else {
          onDecomposed?.()
          setIsDecomposing(false)
        }
      } catch (e) {
        if (isDecomposeStillRunning(e)) {
          // Not a failure: keep the spinner, say so, and poll until it fills.
          setDecomposeNotice(DECOMPOSE_STILL_RUNNING_MESSAGE)
          setAwaitingDecompose(true)
        } else {
          setDecomposeError(decomposeErrorMessage(e))
          setIsDecomposing(false)
        }
      }
    }
    return (
      <div data-tour="combination-matrix">
        <EmptyState
          icon={isDecomposing ? <Loader2 className="w-7 h-7 animate-spin" /> : <Sparkles className="w-7 h-7" />}
          title="No extracted features yet"
          description="The matrix needs your idea broken into components before it can place them against domains — extract features to build it."
          action={
            action?.edit_allowed
              ? { label: isDecomposing ? 'Extracting…' : action.label, onClick: handleDecompose }
              : undefined
          }
        />
        {decomposeNotice && (
          <div className="mt-2 p-2 bg-sky-50 border border-sky-200 text-sky-800 text-xs rounded-lg text-center flex items-center justify-center gap-2" role="status" data-testid="decompose-still-running">
            <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            <span>{decomposeNotice}</span>
          </div>
        )}
        {decomposeError && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg text-center" role="alert">
            {decomposeError}
          </div>
        )}
      </div>
    )
  }

  if (matrix.cells.length === 0 || !matrix.legend) {
    return (
      <div data-tour="combination-matrix" data-testid="combination-matrix-empty">
        <EmptyState
          icon={<Sparkles className="w-7 h-7" />}
          title="No combination data yet"
          description="Run a scan to populate the matrix."
        />
      </div>
    )
  }

  const legend = matrix.legend
  const grid = buildMatrixGrid(matrix.features, matrix.domains, matrix.cells)
  const whiteSpaceCount = matrix.cells.filter((c) => c.is_white_space).length
  const selected = selectedKey
    ? matrix.cells.find((c) => cellKey(c) === selectedKey) ?? null
    : null

  return (
    <div className="flex gap-4 flex-col lg:flex-row" data-testid="combination-matrix" data-tour="combination-matrix">
      {/* Only the TABLE scrolls sideways (below). The card itself must not:
          when the card was the scroll container, scroll-snap pulled the first
          domain column to the card's edge on load, hiding the heading and the
          left of the sticky feature column (seen live 6 Sep 2026). */}
      <div className="light-card p-4 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <h3 className="text-sm font-bold text-iflow-900 flex items-center gap-1">
            Inventive features × application domains — where is nobody filing?
            <HelpPopover topic="ws-matrix" size={14} />
          </h3>
          <span className="ml-auto px-2 py-0.5 text-[10px] rounded-full bg-sky-500/10 text-gray-600">
            Rows: {matrix.features.length} features
          </span>
          <span className="px-2 py-0.5 text-[10px] rounded-full bg-violet-500/10 text-violet-700">
            Columns: {matrix.domains.length} domains
          </span>
        </div>
        <HowToReadThisCard
          visualisationId="whitespace-matrix"
          title="The Combination Matrix"
          body="Each cell is the share of a feature's own matched neighbourhood that falls in that domain, not a raw count against the whole corpus. Dashed cells are combination white space: both halves exist, the pairing doesn't."
          example={`Example: a ${matrix.features[0]?.label ?? 'feature'} cell reading “white” against ${matrix.domains[0]?.label ?? 'a domain'} means nobody has combined the two yet — not that either half is rare on its own.`}
        />
        <p className="text-[11.5px] text-gray-600 mb-1">
          Each cell is the share of a feature's own matched neighbourhood that falls in that domain — not a raw count
          against the whole corpus. Dashed cells are combination white space: both halves exist, the pairing doesn't.
        </p>
        <p className="text-[10.5px] text-gray-500 mb-3 italic">{legend.coverage_note}</p>

        {matrix.domains.length > MATRIX_SCROLL_HINT_MIN_DOMAINS && (
          <p className="text-[10.5px] text-gray-500 mb-1" data-testid="matrix-scroll-hint">
            Scroll sideways to see all {matrix.domains.length} domains — the feature column stays put.
          </p>
        )}
        {/* UX review F1: the feature column used to be unbounded and nowrap, so
            forty sentence-long features pushed every domain column but the
            first out of view, with nothing to say more existed. The label
            column is now capped (full text on hover) and sticky, so the
            values stay readable however far the grid scrolls. */}
        {/* scroll-snap on the x axis so a horizontal scroll always lands on a
            whole column — never a half-clipped one that reads as truncated
            values ("73"/"satura" — UX review Finding 1). scrollPaddingLeft is
            the sticky feature column's width, so a snapped column sits just
            right of it; position 0 is itself a snap point (the corner cell),
            so nothing jumps on load. */}
        <div className="overflow-x-auto" data-testid="matrix-scroll" style={{ scrollSnapType: 'x proximity', scrollPaddingLeft: MATRIX_LABEL_COLUMN_PX }}>
        <table className="border-separate" style={{ borderSpacing: 5 }} role="grid" aria-label="Combination matrix">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white" style={{ scrollSnapAlign: 'start' }} />
              {matrix.domains.map((d) => (
                <th key={d.id} className="text-[10.5px] font-semibold text-gray-600 text-left align-bottom px-1 min-w-[80px] max-w-[90px] whitespace-normal break-words" style={{ scrollSnapAlign: 'start' }} title={d.label}>
                  {d.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.map((row, i) => (
              <tr key={matrix.features[i].id}>
                <th scope="row" className="sticky left-0 z-10 bg-white text-[11px] font-semibold text-gray-700 text-left pr-2" data-testid="matrix-feature-label" style={{ scrollSnapAlign: 'start' }}>
                  <span className="block truncate" style={{ maxWidth: MATRIX_LABEL_COLUMN_PX - 8 }} title={matrix.features[i].label}>{matrix.features[i].label}</span>
                </th>
                {row.map((cell) => {
                  const colors = densityBandColor(cell.density_band)
                  const key = cellKey(cell)
                  const isWhite = cell.density_band === 'white'
                  const pct = cell.share != null ? Math.round(cell.share * 100) : null
                  return (
                    <td key={key} className="p-0" style={{ scrollSnapAlign: 'start' }}>
                      <button
                        type="button"
                        role="gridcell"
                        aria-label={`${cell.feature_label} × ${cell.domain_label}: ${pct != null ? `${pct}%` : cell.count} (${cell.density_band})`}
                        aria-pressed={selectedKey === key}
                        onClick={() => setSelectedKey(key)}
                        className="w-20 rounded-lg text-center text-[11px] font-bold flex flex-col items-center justify-center py-1.5"
                        style={{
                          background: colors.background,
                          color: colors.color,
                          border: isWhite ? '1.8px dashed #D97706' : 'none',
                          outline: selectedKey === key ? '3px solid #FBBF24' : undefined,
                          outlineOffset: 1,
                        }}
                      >
                        <span className="block text-sm font-extrabold">{pct != null ? `${pct}%` : cell.count}</span>
                        <span className="text-[9px] font-medium opacity-80">{cell.density_band}</span>
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-3 text-[11px] text-gray-500">
          Share density (this scan's neighbourhood only):
          {legend.bands.map(({ band, label, min_share }) => {
            const colors = densityBandColor(band)
            return (
              <span key={band} className="flex items-center gap-1">
                <i
                  className="inline-block w-[26px] h-[10px] rounded-sm"
                  style={{ background: colors.background, border: band === 'white' ? '1.5px dashed #D97706' : 'none' }}
                />
                {label} {band !== 'white' ? `(≥${Math.round(min_share * 100)}%)` : ''}
              </span>
            )
          })}
          {whiteSpaceCount > 0 && (
            <span className="ml-2 text-amber-700 font-semibold">⚠ {whiteSpaceCount} white cells found</span>
          )}
        </div>
      </div>

      <div className="w-full lg:w-[300px] flex flex-col gap-3">
        <div className="light-card p-4" data-testid="matrix-cell-detail">
          {selected ? (
            <>
              <div className="text-[13.5px] font-extrabold text-iflow-900">
                {selected.feature_label} × {selected.domain_label}
              </div>
              <div className="text-[11px] text-gray-500 mb-2">
                Selected cell {selected.is_white_space ? '· combination white space' : ''}
              </div>
              <p className="text-xs text-gray-700 leading-relaxed">{cellNarrative(selected)}</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => onExploreAsOpportunity?.(selected)}
                  className="px-2.5 py-1 text-[11px] font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors"
                >
                  Explore as opportunity
                </button>
                <button
                  onClick={() => onRunNoveltyProbe?.(selected)}
                  className="px-2.5 py-1 text-[11px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors"
                >
                  Run novelty probe
                </button>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-500">Select a cell to see its detail.</p>
          )}
        </div>
        <div className="light-card p-4">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Column sources</div>
          <p className="text-[11.5px] text-gray-700 mt-1.5 leading-relaxed">
            Domains are drawn from the CPC section and subject-topic metadata of the records your last scan already
            explored — not a fresh corpus search.
          </p>
        </div>
      </div>
    </div>
  )
}
